import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  /** @type {AbortController | undefined} */
  #variantGalleryController;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#variantGalleryController?.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;
    const variantId = event.detail.resource?.id;

    if (variantId && await this.refreshVariant(variantId)) return;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  async refreshVariant(variantId) {
    if (this.dataset.treuespurCurated !== 'true' || !variantId || !this.dataset.productUrl) return false;
    {
      this.#variantGalleryController?.abort();
      this.#variantGalleryController = new AbortController();

      try {
        const url = new URL(this.dataset.productUrl, window.location.origin);
        url.searchParams.set('variant', variantId);

        if (this.dataset.sectionId) {
          url.searchParams.set('section_id', this.dataset.sectionId);
        }

        const currentUrl = new URL(window.location.href);
        const previewThemeId = currentUrl.searchParams.get('preview_theme_id');
        if (previewThemeId) url.searchParams.set('preview_theme_id', previewThemeId);

        const response = await fetch(url.toString(), {
          signal: this.#variantGalleryController.signal,
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!response.ok) throw new Error(`Treuespur variant gallery request failed: ${response.status}`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const handle = this.dataset.productHandle;
        const candidates = Array.from(html.querySelectorAll('media-gallery[data-treuespur-curated="true"]'));
        const newMediaGallery =
          candidates.find((gallery) => gallery instanceof HTMLElement && gallery.dataset.productHandle === handle) ??
          candidates[0];

        if (newMediaGallery && newMediaGallery.dataset.selectedVariantId === String(variantId)) {
          this.replaceWith(newMediaGallery);
          return true;
        }
      } catch (error) {
        if (error?.name === 'AbortError') return false;
        console.error(error);
      }
    }
    return false;
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
